<script setup>
import { computed } from 'vue';
import { Layers, ChevronDown } from 'lucide-vue-next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { i18n } from '@/i18n';
import Tooltip2 from '@/components/Tooltip2.vue';

const props = defineProps({
  count: Number,
  open: Boolean,
});

const emit = defineEmits(['update:count', 'update:open']);

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});
const options = [
  { value: 1, label: '1', description: 'create.batch.single' },
  { value: 2, label: '2', description: 'create.batch.double' },
  { value: 4, label: '4', description: 'create.batch.quad' },
  { value: 9, label: '9', description: 'create.batch.nine' },
];

function $t(k, v, d) {
  return i18n.get(k, v, d);
}

const tooltipText = computed(() => {
  return props.count === 1
    ? $t('create.batch.generateOne')
    : $t('create.batch.generateMultiple', { count: props.count });
});

const popoverTitle = computed(() => {
  return $t('create.batch.title');
});

const getButtonLabel = (option) => {
  return option === 1
    ? $t('create.batch.generateOne')
    : $t('create.batch.generateMultiple', { count: option });
};

function selectCount(value) {
  emit('update:count', value);
  isOpen.value = false;
}

function handleBatchItemMouseEnter(event, optionValue) {
  if (props.count !== optionValue) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleBatchItemMouseLeave(event, optionValue) {
  if (props.count !== optionValue) {
    event.currentTarget.style.backgroundColor = '';
  }
}

defineExpose({
  getCount: () => props.count,
});
</script>

<template>
  <Tooltip2 :text="tooltipText">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <button
          class="h-8 sm:h-9 select-none px-2 pr-1 bg-white rounded-full text-black/60 transition-all duration-200 flex items-center justify-center gap-1 cursor-pointer border-[0.5px] border-black/5 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20 focus:outline-none focus:ring-0 group active:scale-95"
          :title="tooltipText"
          :aria-label="tooltipText"
          :aria-expanded="isOpen"
          aria-haspopup="true"
        >
          <div class="shrink-0 size-6 flex items-center justify-center">
            <Layers class="size-3.5 text-black/60" />
          </div>
          <span
            class="text-[13px] font-medium text-black/60 group-hover:text-black/90 transition-colors"
            >{{ count }}</span
          >
          <ChevronDown
            class="size-4 transition-transform duration-200 text-black/30 group-hover:text-black/90"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        class="w-56 p-0 rounded-[20px] backdrop-blur-[60px]"
        style="
          background-color: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
        "
        side="bottom"
        :side-offset="8"
        :align="'start'"
        @openAutoFocus="(e) => e.preventDefault()"
      >
        <!-- Header Section -->
        <div class="px-4 py-3 border-b border-black/5">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-normal text-black/90">
              {{ popoverTitle }}
            </h3>
          </div>
        </div>

        <!-- Options Grid -->
        <div class="p-2">
          <div class="grid grid-cols-2 gap-1">
            <button
              v-for="option in options"
              :key="option.value"
              @click="selectCount(option.value)"
              class="group relative flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 active:scale-95 mb-0.5 bg-white border"
              :class="count === option.value ? 'border-black/10' : 'border-black/5'"
              :style="count === option.value ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
              @mouseenter="handleBatchItemMouseEnter($event, option.value)"
              @mouseleave="handleBatchItemMouseLeave($event, option.value)"
              :title="getButtonLabel(option.value)"
              :aria-label="getButtonLabel(option.value)"
              :aria-pressed="count === option.value"
            >
              <!-- Number Display -->
              <div
                class="text-lg font-medium mb-1 transition-colors"
                :class="{
                  'text-black': count === option.value,
                  'text-black/50': count !== option.value,
                }"
              >
                {{ option.label }}
              </div>

              <!-- Description -->
              <div
                class="text-sm font-normal transition-colors"
                :class="{
                  'text-black': count === option.value,
                  'text-black/50': count !== option.value,
                }"
              >
                {{
                  option.value === 1
                    ? $t('create.batch.singleImage', 'Single')
                    : $t('create.batch.multipleImages', 'Images')
                }}
              </div>

              <!-- Selected Indicator -->
              <div
                v-if="count === option.value"
                class="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-md"
                aria-hidden="true"
              >
                <svg
                  class="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </Tooltip2>
</template>
